import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {firstValueFrom, Subject} from "rxjs";
import {environment} from '../../../environments/environment.js';
import {SnackbarService} from '../snackbar.service.js';
import {SessionService} from "../session.service";
import {PageLoaderService} from "../page-loader.service";
import {PatientRow} from "allai-admin-shared/dist/util/patient/patient-row";
import {PatientsFilterService} from "./patient-filter.service";
import {PatientDetail, PatientDetailResponse, PatientsResponse} from "allai-admin-shared";
import {ActivatedRouteSnapshot} from "@angular/router";

/** Service that loads and caches all organizations in the organization list page */
@Injectable({
	providedIn: 'root'
})
export class PatientsService {
	/** Loaded organizations by id */
	private _patientList:PatientRow[];
	private _total:number = 100;
	private _patientDetail:PatientDetail;

	private newPatientsSubject = new Subject<void>();
	private newPatientDetailSubject = new Subject<void>();

	constructor(private http:HttpClient,
	            private sessionService:SessionService,
	            private patientsFilterService:PatientsFilterService,
	            private pageLoaderService:PageLoaderService,
	            private snackbar:SnackbarService) {

		//clear things out if the patient logs out
		this.sessionService.onLogout.subscribe(() => {
			this.clear();
		});

		//listen to filter changes
		this.patientsFilterService.onNewFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolvePatients());
		});
	}

	/** Resolver to get all organizations for the list if not already loaded */
	static resolvePatients():Promise<PatientRow[]> {
		return inject(PatientsService).resolvePatients();
	}

	/** Resolver to get the appointment that is clicked on */
	static resolvePatientDetail = async(route:ActivatedRouteSnapshot) => {
		return inject(PatientsService).resolvePatientDetail(route.params["id"]);
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolvePatients():Promise<PatientRow[]> {
		try {
			const params:HttpParams = this.patientsFilterService.params;

			//load from server
			const response = await firstValueFrom(this.http.get<PatientsResponse>(`${environment.apiUrl}/patients`, {params}));

			this._patientList = response.patients;
			this._total = response.total;

			//dispatch that there was a change
			this.newPatientsSubject.next();

		} catch(e) {
			//clear filter on error, just in case
			this.patientsFilterService.clear();

			console.error("Failed to load patients from server", e);
			this.snackbar.error("Failed to download patients");
		}

		return this._patientList;
	}

	/** Resolver to get all organizations for the list if not already loaded */
	async resolvePatientDetail(patientId?:string):Promise<PatientDetail> {

		try {
			const id = patientId ?? this._patientDetail.patient.id;
			const patientResponse = await firstValueFrom(this.http.get<PatientDetailResponse>(`${environment.apiUrl}/patients/${id}`));

			this._patientDetail = {
				patient: patientResponse.patient,
				organization: patientResponse.organization,
				appointments: patientResponse.appointments
			}
			this.newPatientDetailSubject.next();

		} catch(ignore) {
		}
		return this._patientDetail;
	}

	get onNewPatients() {
		return this.newPatientsSubject.asObservable();
	}

	/** Clear out loaded organizations and force a reload */
	clear() {
		this._patientList = undefined;
		this._total = 100;
	}

	/** Get the sorted organizations */
	get patientList() {
		return this._patientList;
	}

	/** Get the organization detail */
	get patientDetail() {
		return this._patientDetail;
	}

	get total():number {
		return this._total;
	}

	set total(total:number) {
		this._total = total;
	}
}
